import { supabase } from '../../../supabase';

// Every fee read/write is one Postgres function. No table access from the UI.
// Allocation order, journal posting, numbering and locking all happen server-side.

export class FeeApiError extends Error {
  constructor(message, code = null, details = null) {
    super(message);
    this.name = 'FeeApiError';
    this.code = code;
    this.details = details;
  }
}

const clean = (msg) => String(msg || 'Request failed').replace(/^.*?ERROR:\s*/i, '').trim();

async function call(fn, args = {}) {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new FeeApiError(clean(error.message), error.code || null, error.details || null);
  return data;
}

export function toISODate(d = new Date()) {
  if (typeof d === 'string') return d.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Reads ───────────────────────────────────────────────────
// month/year null → server picks the active period. familyId → single family row.
export const getCollection = ({ month = null, year = null, familyId = null } = {}) =>
  call('fee_collection', { p_month: month, p_year: year, p_family_id: familyId });

export const getFamilyDues = (familyId) =>
  call('fee_family_dues', { p_family_id: familyId });

export const getFamilyLedger = (familyId) =>
  call('fee_ledger', { p_family_id: familyId });

// kind: 'defaulters' | 'cashbook' | 'class_collection' | 'head_revenue' | 'aging'
export const getReport = ({ kind, from = null, to = null, classId = null }) =>
  call('fee_report', {
    p_kind: kind,
    p_from: from ? toISODate(from) : null,
    p_to: to ? toISODate(to) : null,
    p_class_id: classId,
  });

// What looks wrong in a generated period, versus the period before it.
export const getGenerationExceptions = ({ month, year, discountPct = null }) =>
  call('fee_generate_exceptions', { p_month: month, p_year: year, p_discount_pct: discountPct });

export const previewFines = ({ month, year, amount = null }) =>
  call('fee_fine_preview', { p_month: month, p_year: year, p_amount: amount });

// ── Period workflow ─────────────────────────────────────────
export const generatePeriod = ({ month, year, months = 1, dueDate = null, toggles = {}, special = [] }) =>
  call('fee_generate', {
    p_month: month, p_year: year, p_months: months,
    p_due_date: dueDate ? toISODate(dueDate) : null,
    p_toggles: toggles, p_special: special,
  });

export const discardDraft = ({ month, year }) =>
  call('fee_discard_draft', { p_month: month, p_year: year });

// status: 'approved' | 'published'
export const setPeriodStatus = ({ month, year, status }) =>
  call('fee_set_status', { p_month: month, p_year: year, p_status: status });

export const closePeriod = ({ month, year }) =>
  call('fee_close', { p_month: month, p_year: year });

export const applyFines = ({ month, year, amount, studentIds }) =>
  call('fee_fine_apply', { p_month: month, p_year: year, p_amount: amount, p_student_ids: studentIds });

// ── Money ───────────────────────────────────────────────────
export const recordPayment = ({ familyId, itemIds, cash, method, date = null, note = null, useAdvance = true, reference = null }) =>
  call('fee_record_payment', {
    p_family_id: familyId, p_item_ids: itemIds, p_cash: Number(cash) || 0, p_method: method,
    p_date: toISODate(date || new Date()), p_note: note || null,
    p_use_advance: !!useAdvance, p_reference: reference || null,
  });

export const reversePayment = ({ paymentId, reason }) =>
  call('fee_reverse_payment', { p_payment_id: paymentId, p_reason: reason });

export const voidItems = ({ itemIds, reason }) =>
  call('fee_void_items', { p_item_ids: itemIds, p_reason: reason });

// waivers: [{ itemId, amount }]
export const waiveItems = ({ waivers, reason }) =>
  call('fee_waive_items', {
    p_waivers: waivers.map(w => ({ item_id: w.itemId, amount: Number(w.amount) || 0 })),
    p_reason: reason,
  });

export const depositAdvance = ({ familyId, amount, method, note = null, date = null }) =>
  call('fee_deposit_advance', {
    p_family_id: familyId, p_amount: Number(amount) || 0, p_method: method,
    p_note: note || null, p_date: toISODate(date || new Date()),
  });

// ── Lookups (small, cached by the caller) ───────────────────
export async function getFeeHeads() {
  const { data, error } = await supabase
    .from('fee_heads')
    .select('id, name, type, order_index, is_active')
    .order('order_index', { ascending: true, nullsFirst: false });
  if (error) throw new FeeApiError(clean(error.message), error.code || null);
  return (data || []).filter(h => h.is_active !== false);
}

export async function getClasses() {
  const { data, error } = await supabase
    .from('classes')
    .select('id, name, order_num')
    .order('order_num', { ascending: true, nullsFirst: false });
  if (error) throw new FeeApiError(clean(error.message), error.code || null);
  return data || [];
}
